import { useEffect, useRef, useCallback } from 'react';
import { doc, updateDoc, serverTimestamp } from 'firebase/firestore';
import { db } from '../firebase';
import { locationSocket } from '../services/locationSocket';

export interface LiveUserLocation {
  lat: number;
  lng: number;
  address: string;
  lastUpdated: Date;
  sharingEnabled: boolean;
}

interface UseLiveLocationOptions {
  enabled: boolean;
  connectionId: string;
  userId: string;
  userName: string;
  userKey: 'user1' | 'user2';
  onMyLocation: (location: LiveUserLocation) => void;
  onPartnerLocation: (userKey: 'user1' | 'user2', location: LiveUserLocation) => void;
  partnerUserKey: 'user1' | 'user2';
}

async function reverseGeocode(lat: number, lng: number): Promise<string> {
  try {
    const res = await fetch(
      `https://nominatim.openstreetmap.org/reverse?format=json&lat=${lat}&lon=${lng}`,
      { headers: { 'Accept-Language': 'nl' } }
    );
    const data = await res.json();
    return data.display_name || 'Onbekende locatie';
  } catch {
    return 'Onbekende locatie';
  }
}

export function useLiveLocation({
  enabled,
  connectionId,
  userId,
  userName,
  userKey,
  onMyLocation,
  onPartnerLocation,
  partnerUserKey,
}: UseLiveLocationOptions) {
  const watchIdRef = useRef<number | null>(null);
  const lastFirestoreWriteRef = useRef<number>(0);
  const lastSocketSendRef = useRef<number>(0);

  const connectionRef = doc(db, 'connections', connectionId);

  const stopTracking = useCallback(() => {
    if (watchIdRef.current !== null) {
      navigator.geolocation.clearWatch(watchIdRef.current);
      watchIdRef.current = null;
    }
    locationSocket.disconnect();
  }, []);

  useEffect(() => {
    if (!enabled) {
      stopTracking();
      return;
    }

    if (!navigator.geolocation) return;

    // Connect socket
    locationSocket.connect(connectionId, userId, userName);

    // Listen for partner location via socket (real-time)
    locationSocket.onPartnerLocation((update) => {
      onPartnerLocation(partnerUserKey, {
        lat: update.lat,
        lng: update.lng,
        address: '',
        lastUpdated: new Date(update.timestamp),
        sharingEnabled: true,
      });
    });

    // Start GPS watch
    watchIdRef.current = navigator.geolocation.watchPosition(
      async (position) => {
        const { latitude: lat, longitude: lng, accuracy } = position.coords;
        const nowMs = Date.now();

        // Flag low-accuracy positions (IP-based) but still use them so the avatar
        // shows on the map. The strict accuracy check stays in verifyGPSLocation.
        const isLowAccuracy = accuracy > 5000;

        // 1. Socket: max elke 5 seconden (real-time voor partner)
        if (nowMs - lastSocketSendRef.current >= 5000) {
          lastSocketSendRef.current = nowMs;
          locationSocket.sendLocation(connectionId, userId, lat, lng);
        }

        // 2. Lokale state: altijd updaten
        const address = isLowAccuracy ? 'Locatie onnauwkeurig' : await reverseGeocode(lat, lng);
        const location: LiveUserLocation = {
          lat,
          lng,
          address,
          lastUpdated: new Date(),
          sharingEnabled: true,
        };
        onMyLocation(location);

        // 3. Firestore: direct bij eerste positie, daarna max elke 60s
        // First write ensures location survives page refresh/remount
        if (lastFirestoreWriteRef.current === 0 || nowMs - lastFirestoreWriteRef.current >= 60000) {
          lastFirestoreWriteRef.current = nowMs;
          try {
            await updateDoc(connectionRef, {
              [`physicalMeeting.userLocations.${userKey}`]: {
                lat,
                lng,
                address,
                lastUpdated: serverTimestamp(),
                sharingEnabled: true,
              },
            });
          } catch (e) {
            console.error('[useLiveLocation] Firestore write error:', e);
          }
        }
      },
      (error) => console.error('[useLiveLocation] GPS error:', error),
      { enableHighAccuracy: true, timeout: 10000, maximumAge: 5000 }
    );

    return () => stopTracking();
  }, [enabled, connectionId, userId, userName, userKey]);

  return { stopTracking };
}
